import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDatetime = (val) => {
  if (!val) return '---';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Rome',
    }).format(new Date(val));
  } catch { return val; }
};

const fmtDate = (val) => {
  if (!val) return '---';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(new Date(val));
  } catch { return val; }
};

// Config colori per tipo intervento
const getTipoConfig = (tipo) => {
  const t = tipo?.toUpperCase() || '';
  if (t === 'MONTATO')  return {
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10 border-emerald-500/20',
    dot: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,.5)]',
    icon: 'ArrowUpRight',
    label: 'MONTATO',
  };
  if (t === 'SMONTATO') return {
    color: 'text-red-500',
    bg: 'bg-red-500/10 border-red-500/20',
    dot: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,.5)]',
    icon: 'ArrowDownRight',
    label: 'SMONTATO',
  };
  return {
    color: 'text-slate-400',
    bg: 'bg-slate-400/10 border-slate-400/20',
    dot: 'bg-slate-400',
    icon: 'Minus',
    label: tipo || 'N/D',
  };
};

// ─── Riga storico ─────────────────────────────────────────────────────────────
const StoricoRow = ({ row }) => {
  const cfg = getTipoConfig(row.tipointervento);

  return (
    <div className="flex flex-col gap-3 p-5 rounded-[2rem] bg-muted/20 border border-border/40 hover:border-primary/20 transition-all duration-300">

      {/* Top row: icona + data + tipo badge */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`shrink-0 p-2 rounded-xl border ${cfg.bg} ${cfg.color}`}>
            <Icon name={cfg.icon} size={14} strokeWidth={3} />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50">
              Data Intervento
            </span>
            <span className="text-sm font-bold text-foreground/80 font-mono">
              {fmtDatetime(row.data_intervento)}
            </span>
          </div>
        </div>

        {/* Badge tipo intervento */}
        <div className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border ${cfg.bg}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
          <span className={`text-[10px] font-black uppercase tracking-widest ${cfg.color}`}>
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Dati secondari: linea, colata, posizione */}
      <div className="pl-11 grid grid-cols-3 gap-3">
        {[
          { label: 'Linea',     val: row.linea     },
          { label: 'Colata',    val: row.colata    },
          { label: 'Posizione', val: row.posizione },
        ].map(({ label, val }) => (
          <div key={label} className="flex flex-col gap-0.5">
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">{label}</span>
            <span className="text-[12px] font-bold text-foreground/70">{val || '---'}</span>
          </div>
        ))}
      </div>

      {/* Dati aggiuntivi: stato, stato RID, causa freno */}
      <div className="pl-11 grid grid-cols-3 gap-3">
        {[
          { label: 'Stato',       val: row.stato },
          { label: 'Stato RID',   val: row.stato_rid },
          { label: 'Causa Freno', val: row.causa_freno ? 'SI' : 'NO' },
        ].map(({ label, val }) => (
          <div key={label} className="flex flex-col gap-0.5">
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">{label}</span>
            <span className="text-[12px] font-bold text-foreground/70">{val || '---'}</span>
          </div>
        ))}
      </div>

      {/* Note */}
      {row.note && (
        <div className="pl-11">
          <div className="p-3 rounded-xl bg-background/50 border border-border/20">
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 block mb-1">
              Note
            </span>
            <p className="text-xs font-bold text-foreground/70 leading-relaxed italic">
              "{row.note}"
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Componente principale ────────────────────────────────────────────────────
// nMacchina: valore di n_macchina della raddrizzatrice di cui vogliamo lo storico
const StoricoRaddrizzatrice = ({ nMacchina, nome, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Fetch da rd_cco filtrando per n_macchina ──────────────────────────────
  useEffect(() => {
    const fetchHistory = async () => {
      if (!nMacchina) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('rd_cco')
          .select('id, data_intervento, tipointervento, linea, colata, posizione, stato, stato_rid, causa_freno, data_rientro, note, n_macchina')
          .eq('n_macchina', nMacchina)
          .order('data_intervento', { ascending: false });

        if (error) throw error;
        setHistory(data || []);
      } catch (err) {
        console.error('Errore fetch storico raddrizzatrice:', err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [nMacchina]);

  // ── Escape + blocca scroll ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const generatePDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

      const tableColumn = [
        'DATA INTERVENTO',
        'TIPO',
        'LINEA',
        'COLATA',
        'POSIZIONE',
        'STATO',
        'DATA RIENTRO',
        'NOTE',
      ];

      const tableRows = history.map(row => [
        fmtDatetime(row.data_intervento),
        row.tipointervento || '---',
        row.linea          || '---',
        row.colata          || '---',
        row.posizione        || '---',
        row.stato            || '---',
        fmtDate(row.data_rientro),
        row.note              || '---',
      ]);

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text('OrizonDesk', 14, 15);

      doc.setFontSize(20);
      doc.setTextColor(15, 23, 42);
      doc.text('STORICO INTERVENTI RADDRIZZATRICE', 14, 25);

      doc.setDrawColor(226, 232, 240);
      doc.line(14, 28, 283, 28);

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.setFont('helvetica', 'bold');
      doc.text('MACCHINA:', 14, 36);
      doc.setFont('helvetica', 'normal');
      doc.text(`${nome || nMacchina || 'N/D'}`, 42, 36);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 48,
        margin: { left: 14, right: 14, bottom: 20 },
        theme: 'striped',
        headStyles: {
          fillColor: [15, 23, 42],
          textColor: 255,
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center',
        },
        bodyStyles: { fontSize: 8, cellPadding: 4 },
        columnStyles: {
          1: { halign: 'center', fontStyle: 'bold' }, // Tipo
          2: { halign: 'center' },
          3: { halign: 'center' },
          4: { halign: 'center' },
          5: { halign: 'center' },
          6: { halign: 'center' },
          7: { cellWidth: 'auto' }, // Note
        },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 1) {
            const val = (data.cell.raw || '').toString().toUpperCase();
            if (val === 'MONTATO')  data.cell.styles.textColor = [16, 185, 129];
            if (val === 'SMONTATO') data.cell.styles.textColor = [239, 68, 68];
          }
        },
        didDrawPage: () => {
          const pageSize = doc.internal.pageSize;
          const pageW = pageSize.width || pageSize.getWidth();
          const pageH = pageSize.height || pageSize.getHeight();

          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.setFont('helvetica', 'normal');

          doc.text(`Data stampa: ${new Date().toLocaleString('it-IT')}`, 14, pageH - 10);

          const str = `Pagina ${doc.internal.getNumberOfPages()}`;
          doc.text(str, pageW / 2, pageH - 10, { align: 'center' });

          const url = 'https://desk.orizon-aix.com';
          doc.text(url, pageW - 14, pageH - 10, { align: 'right' });
        },
      });

      doc.save(`Storico_Raddrizzatrice_${nMacchina}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF Error:', err);
      alert('Errore PDF: ' + err.message);
    }
  };

  // ── Statistiche rapide ────────────────────────────────────────────────────
  const totMontati  = history.filter(r => r.tipointervento?.toUpperCase() === 'MONTATO').length;
  const totSmontati = history.filter(r => r.tipointervento?.toUpperCase() === 'SMONTATO').length;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-0 md:p-6 overflow-hidden">

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Pannello */}
      <div className="relative w-full h-full md:h-auto md:max-h-[88vh] md:max-w-xl bg-card border-t md:border border-border/60 rounded-t-[2.5rem] md:rounded-[2.5rem] shadow-2xl flex flex-col animate-in slide-in-from-bottom-10 md:zoom-in-95 duration-300">

        {/* Drag handle mobile */}
        <div className="md:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-12 h-1.5 rounded-full bg-muted/50" />
        </div>

        {/* ── HEADER ── */}
        <div className="shrink-0 px-7 py-6 border-b border-border/40 bg-gradient-to-b from-primary/5 to-transparent">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1.5 min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-[10px] font-black uppercase tracking-[.3em] text-primary/60">
                  Timeline Interventi
                </span>
              </div>
              <h3 className="font-black uppercase tracking-tighter text-foreground text-xl leading-tight break-words">
                {nome || 'Raddrizzatrice'}
              </h3>
              <span className="font-mono text-[11px] font-bold text-muted-foreground/50 bg-muted/50 px-2 py-0.5 rounded-lg w-fit">
                N° {nMacchina}
              </span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* PDF */}
              <button
                type="button"
                onClick={generatePDF}
                disabled={history.length === 0}
                className="w-10 h-10 rounded-2xl bg-primary/10 border border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                title="Scarica PDF"
              >
                <Icon name="FileDown" size={18} />
              </button>
              {/* Chiudi */}
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-2xl bg-muted/50 hover:bg-red-500/10 border border-border/40 text-muted-foreground hover:text-red-500 hover:border-red-500/20 flex items-center justify-center transition-all shadow-sm"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>

          {/* Statistiche rapide */}
          {!loading && history.length > 0 && (
            <div className="flex gap-3 mt-4">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <Icon name="ArrowUpRight" size={12} className="text-emerald-500" strokeWidth={3} />
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-500">
                  {totMontati} Montati
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-red-500/10 border border-red-500/20">
                <Icon name="ArrowDownRight" size={12} className="text-red-500" strokeWidth={3} />
                <span className="text-[10px] font-black uppercase tracking-widest text-red-500">
                  {totSmontati} Smontati
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-muted/40 border border-border/30 ml-auto">
                <Icon name="List" size={12} className="text-muted-foreground/50" />
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50">
                  {history.length} totali
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ── BODY ── */}
        <div
          className="flex-1 overflow-y-auto p-5 md:p-6 space-y-4"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.15) transparent' }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">
                Caricamento storico...
              </span>
            </div>
          ) : history.length > 0 ? (
            history.map((row) => (
              <StoricoRow key={row.id} row={row} />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-24 border-2 border-dashed border-border/40 rounded-[2.5rem] bg-muted/5">
              <Icon name="History" size={40} className="text-muted-foreground/20 mb-4" />
              <span className="text-sm font-bold text-muted-foreground/40 uppercase tracking-widest">
                Nessun intervento registrato
              </span>
              <span className="text-[10px] font-bold text-muted-foreground/30 uppercase tracking-widest mt-1">
                per N° {nMacchina}
              </span>
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="shrink-0 px-7 py-5 border-t border-border/40 bg-muted/10 flex items-center justify-between gap-3">
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/30">
            {loading ? '...' : `${history.length} interventi registrati`}
          </span>
          <button
            onClick={onClose}
            className="px-7 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] hover:bg-border/60 transition-all border border-border/40"
          >
            Chiudi
          </button>
        </div>

      </div>
    </div>
  );
};

export default StoricoRaddrizzatrice;